import { Pagination, PaginationResponse, User, UserCreatePayload, UserUpdatePayload } from '@ekkogmbh/apisdk';
import { Add, Delete, Edit, SmartToyOutlined, SupervisedUserCircle } from '@mui/icons-material';
import { Button, Chip, Divider, Drawer, Stack, Tooltip, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminTable, AdminTableColumn, Highlight } from 'src/Common/Components/AdminTable';
import { RowActionsMenu } from 'src/Common/Components/RowActionsMenu';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { CenteredColumn } from 'src/Common/Components/Stacks';
import { UserAvatar } from 'src/Common/Components/UserAvatar';
import { AuthMechanisms } from 'src/Common/Helper/AuthMechanisms';
import useRequest from 'src/Common/Hooks/useRequest';
import Permissions from 'src/Common/Permissions';
import useEkko from 'src/Common/Providers/EkkoProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { orange, red } from '@mui/material/colors';
import { SettingsSection } from '../SettingsSection';
import { UsersAdminPanel } from './UsersAdminPanel';

const DRAWER_WIDTH = 520;

type DialogKind = 'delete' | 'valid' | undefined;

export const UsersAdminSection = (): React.JSX.Element => {
  const { api, hasPermission, config } = useEkko();
  const navigate = useNavigate();
  const { refresh: triggerRefresh } = usePagination();
  const [, startSaving] = useRequest<User>();

  const [drawerUser, setDrawerUser] = useState<User | undefined>();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [dialog, setDialog] = useState<DialogKind>();
  const [dialogUser, setDialogUser] = useState<User | undefined>();

  const canWrite = useMemo(() => hasPermission(Permissions.USERS_WRITE), [hasPermission]);
  const canMappings = useMemo(() => hasPermission(Permissions.MAPPINGS_READ), [hasPermission]);

  const fetchItems = useCallback(
    (pagination: Pagination): Promise<PaginationResponse<User>> => api.getUsers(pagination, true, true),
    [api],
  );

  const openCreate = useCallback(() => {
    setDrawerUser(undefined);
    setIsDrawerOpen(true);
  }, []);

  const openEdit = useCallback((user: User) => {
    setDrawerUser(user);
    setIsDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setDrawerUser(undefined);
  }, []);

  const closeDialog = useCallback(() => {
    setDialog(undefined);
    setDialogUser(undefined);
  }, []);

  const saveHandler = useCallback(
    async (user: UserUpdatePayload | UserCreatePayload, password?: string) => {
      const isUpdate = (user as UserUpdatePayload).id !== undefined;
      startSaving(
        () =>
          isUpdate
            ? api.updateUser(user as UserUpdatePayload, password)
            : api.addUser(user as UserCreatePayload, password),
        () => {
          enqueueSnackbar(isUpdate ? 'User updated.' : 'User created.');
          closeDrawer();
          triggerRefresh();
        },
      );
    },
    [api, startSaving, closeDrawer, triggerRefresh],
  );

  const onDeleteConfirm = useCallback(async () => {
    if (!dialogUser?.id) {
      return;
    }
    await api.deleteUser(dialogUser);
    closeDialog();
    triggerRefresh();
  }, [api, dialogUser, closeDialog, triggerRefresh]);

  const onValidUntilConfirm = useCallback(async () => {
    if (
      !dialogUser ||
      !dialogUser.id ||
      !dialogUser.username ||
      !dialogUser.coordinate ||
      !dialogUser.email ||
      !dialogUser.validUntil
    ) {
      return;
    }
    await saveHandler({
      id: dialogUser.id,
      username: dialogUser.username,
      email: dialogUser.email,
      coordinate: dialogUser.coordinate,
      validUntil: dialogUser.validUntil,
    });
    closeDialog();
  }, [dialogUser, saveHandler, closeDialog]);

  const onChangeValidUntil = useCallback(
    (date: Moment | null) => {
      if (!dialogUser || date === null) {
        return;
      }
      setDialogUser({ ...dialogUser, validUntil: date.format('YYYY-MM-DD') });
    },
    [dialogUser],
  );

  const columns = useMemo<AdminTableColumn<User>[]>(
    () => [
      {
        field: 'username',
        headerName: 'Username',
        size: 220,
        renderCell: (row) => (
          <Stack direction={'row'} alignItems={'center'} spacing={1.5} sx={{ width: '100%' }}>
            <UserAvatar username={row.username} size={'small'} />
            <Typography fontWeight={600} noWrap>
              <Highlight text={row.username} field={'username'} />
            </Typography>
            {!!row.automation && (
              <Tooltip title={'automation user'}>
                <SmartToyOutlined fontSize={'small'} color={'action'} />
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: 'email',
        headerName: 'E-Mail',
        size: 260,
        renderCell: (row) => (
          <Typography
            component={'a'}
            href={`mailto:${row.email}`}
            noWrap
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline', color: 'primary.main' },
            }}
          >
            <Highlight text={row.email} field={'email'} />
          </Typography>
        ),
      },
      {
        field: 'coordinate',
        headerName: 'Coordinate',
        size: 300,
        renderCell: (row) => (
          <Typography variant={'body2'} noWrap color={'text.secondary'}>
            <Highlight text={row.coordinate} field={'coordinate'} />
          </Typography>
        ),
      },
      {
        field: 'validUntil',
        headerName: 'Valid-Until',
        size: 240,
        sortable: false,
        filterable: false,
        renderCell: (row) => {
          if (row.automation) {
            return (
              <Chip
                label={'No expiry'}
                size={'small'}
                sx={{
                  backgroundColor: 'action.selected',
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: 11,
                  height: 20,
                }}
              />
            );
          }
          const m = moment(row.validUntil);
          const hours = m.diff(moment(), 'hours');
          const isExpired = hours <= 0;
          const isSoon = hours > 0 && hours < 48;
          return (
            <Stack direction={'row'} alignItems={'center'} spacing={1}>
              <Tooltip title={'Edit valid-until'}>
                <Typography
                  onClick={() => {
                    if (!canWrite || row.automation) {
                      return;
                    }
                    setDialogUser(row);
                    setDialog('valid');
                  }}
                  sx={{
                    cursor: canWrite ? 'pointer' : 'default',
                    borderBottom: '1px dashed',
                    borderColor: 'text.secondary',
                    '&:hover': canWrite ? { borderColor: 'primary.main', color: 'primary.main' } : undefined,
                  }}
                >
                  {m.format('DD MMM YYYY')}
                </Typography>
              </Tooltip>
              <Chip
                label={m.fromNow()}
                size={'small'}
                sx={{
                  backgroundColor: isExpired ? red[500] : isSoon ? orange[500] : 'action.selected',
                  color: isExpired || isSoon ? 'white' : 'text.secondary',
                  fontWeight: 500,
                  fontSize: 11,
                  height: 20,
                }}
              />
            </Stack>
          );
        },
      },
      {
        field: '__actions',
        headerName: '',
        size: 60,
        sortable: false,
        filterable: false,
        resizable: false,
        hideInColumnsPopover: true,
        align: 'center',
        renderCell: (row) => (
          <RowActionsMenu<User>
            row={row}
            actions={[
              {
                label: 'Roles',
                icon: <SupervisedUserCircle fontSize="small" />,
                onClick: (u) => navigate('/me/admin/users/' + u.id + '/mappings'),
                disabled: () => !canMappings,
              },
              {
                label: 'Edit',
                icon: <Edit fontSize="small" />,
                onClick: openEdit,
                disabled: (u) => !canWrite || !!u.automation,
              },
              {
                label: 'Delete',
                icon: <Delete fontSize="small" />,
                onClick: (u) => {
                  setDialogUser(u);
                  setDialog('delete');
                },
                disabled: () => !canWrite,
              },
            ]}
          />
        ),
      },
    ],
    [canMappings, canWrite, navigate, openEdit],
  );

  const dialogElement = useMemo(() => {
    if (!dialog || !dialogUser) {
      return undefined;
    }
    if (dialog === 'delete') {
      return (
        <GenericDialog
          open
          fullWidth
          centered
          timedOkButton
          type={'confirmation'}
          maxWidth={'sm'}
          title={'Delete User'}
          text={
            <CenteredColumn>
              <Typography>{'Delete User "' + dialogUser.username + '"?'}</Typography>
              <Typography style={{ margin: 16 }} variant={'caption'}>
                This action can not be undone.
              </Typography>
            </CenteredColumn>
          }
          onClose={closeDialog}
          onConfirm={onDeleteConfirm}
        />
      );
    }
    const validUntil = moment(dialogUser.validUntil);
    return (
      <GenericDialog
        open
        type={'confirmation'}
        title={'Update Valid-Until'}
        text={
          <Stack spacing={2} sx={{ minWidth: 300, pt: 1 }}>
            <Stack direction={'row'} spacing={2} alignItems={'center'}>
              <UserAvatar username={dialogUser.username} size={'large'} />
              <Stack spacing={0.5}>
                <Typography fontWeight={600}>{dialogUser.username}</Typography>
                <Typography variant={'body2'} color={'text.secondary'}>
                  {dialogUser.email}
                </Typography>
              </Stack>
            </Stack>
            <Divider />
            <DatePicker
              label={'Valid-Until'}
              disablePast
              value={validUntil}
              onChange={onChangeValidUntil}
              minDate={moment().add(1, 'd')}
              sx={{ width: '100%' }}
            />
          </Stack>
        }
        onClose={closeDialog}
        onConfirm={onValidUntilConfirm}
      />
    );
  }, [dialog, dialogUser, closeDialog, onDeleteConfirm, onValidUntilConfirm, onChangeValidUntil]);

  return (
    <SettingsSection title={'Users'} description={'Manage user accounts, coordinates and validity.'}>
      <AdminTable<User>
        fetchItems={fetchItems}
        columns={columns}
        initialSort={{ field: 'username', sort: 'asc' }}
        sortFieldMap={{
          username: 'U.username',
          email: 'U.email',
          coordinate: 'Node.materializedPath',
        }}
        primaryAction={
          canWrite ? (
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              Add user
            </Button>
          ) : undefined
        }
      />

      <Drawer
        anchor={'right'}
        open={isDrawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: DRAWER_WIDTH }, maxWidth: '100vw' } }}
      >
        <UsersAdminPanel
          user={drawerUser}
          closeHandler={closeDrawer}
          saveHandler={saveHandler}
          passwordEditable={config.api.authMechanism !== AuthMechanisms.OAUTH2}
        />
      </Drawer>

      {dialogElement}
    </SettingsSection>
  );
};
